/**
 * ProcessingChecklist — renders the 14-step processing checklist.
 *
 * Step status is derived from two WS-updated sources on the live file entry:
 *  - completedStepsCount: how many steps have finished (by order in PROCESSING_STEP_ORDER)
 *  - currentStepName: display name of the currently running step
 *
 * Steps at index < completedStepsCount → completed
 * Step whose display_name matches currentStepName → running
 * Everything else → pending
 *
 * The REST detail.steps are used as a fallback for their individual statuses
 * (e.g. failed/skipped steps have status from the REST response).
 *
 * React.memo for performance. AC-3.3
 */
import React, { JSX, memo, useMemo } from 'react';
import Box from '@mui/material/Box';
import StepChecklistRow from './StepChecklistRow';
import {
  PROCESSING_STEP_ORDER,
  STEP_IN_PROGRESS_COPY as STEP_COPY,
} from '../utils/stepLabels';
import type { FileProcessingStep } from 'src/types/aiFileUploadV2';

interface ProcessingChecklistProps {
  liveSteps: FileProcessingStep[];
  currentStepName: string | null;
  completedStepsCount: number;
}

const sx = {
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.75,
  },
};

const ProcessingChecklist = memo(
  ({
    liveSteps,
    currentStepName,
    completedStepsCount,
  }: ProcessingChecklistProps): JSX.Element => {
    const mergedSteps = useMemo(() => {
      const restMap = new Map(liveSteps.map((s) => [s.step_name, s]));

      return PROCESSING_STEP_ORDER.map((stepName, index) => {
        const restStep = restMap.get(stepName);

        // REST step has a terminal status (failed/skipped) — use it
        if (
          restStep &&
          (restStep.status === 'failed' || restStep.status === 'skipped')
        ) {
          return {
            step_name: stepName,
            display_name:
              restStep.display_name ?? STEP_COPY[stepName] ?? stepName,
            status: restStep.status as FileProcessingStep['status'],
          };
        }

        // Derive from WS live data
        let status: FileProcessingStep['status'] = 'pending';
        if (index < completedStepsCount) {
          status = 'completed';
        } else if (
          currentStepName &&
          (STEP_COPY[stepName] ?? stepName) === currentStepName
        ) {
          status = 'running';
        } else if (restStep) {
          status = restStep.status;
        }

        return {
          step_name: stepName,
          display_name:
            restStep?.display_name ?? STEP_COPY[stepName] ?? stepName,
          status,
        };
      });
    }, [liveSteps, currentStepName, completedStepsCount]);

    return (
      <Box sx={sx.list}>
        {mergedSteps.map((step) => (
          <StepChecklistRow
            key={step.step_name}
            displayName={step.display_name}
            status={step.status}
          />
        ))}
      </Box>
    );
  }
);

ProcessingChecklist.displayName = 'ProcessingChecklist';

export default ProcessingChecklist;
